import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { SelectField } from '@/components/ui/select';
import { StepSection } from '@/components/step-section';
import { TextField } from '@/components/text-field';
import { ApiError } from '@/lib/api';
import { field_errors_from, form_error_message } from '@/lib/forms';
import { use_draft } from '@/features/onboarding/hooks';
import { next_module_step_after } from '@/features/onboarding/module-step-router';
import {
  use_adoption_details,
  use_save_adoption_details,
} from '@/features/adoption/hooks';
import {
  ADOPTION_DESIGNATION_OPTIONS,
  ADOPTION_ORG_TYPE_OPTIONS,
  YEAR_BRACKET_OPTIONS,
  type AdoptionDesignation,
  type AdoptionOrgType,
  type YearBracket,
} from '@/features/adoption/types';

type Form = {
  organization_types: AdoptionOrgType[];
  contact_person_name: string;
  designation: AdoptionDesignation | '';
  animals_available: string;
  max_capacity: string;
  volunteer_count: string;
  year_established: YearBracket | '';
  staff_count: string;
};

const EMPTY: Form = {
  organization_types: [],
  contact_person_name: '',
  designation: '',
  animals_available: '',
  max_capacity: '',
  volunteer_count: '',
  year_established: '',
  staff_count: '',
};

function toggle<T>(arr: T[], value: T): T[] {
  return arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
}

/** Keys of `Form` whose value is an array — the multi-select pill fields. */
type ArrayKeys<T> = { [K in keyof T]: T[K] extends unknown[] ? K : never }[keyof T];

function parse_optional_int(
  raw: string,
  min: number,
  max: number,
  label: string,
): { ok: true; value: number | undefined } | { ok: false; error: string } {
  const trimmed = raw.trim();
  if (trimmed === '') return { ok: true, value: undefined };
  const n = Number(trimmed);
  if (!Number.isInteger(n) || n < min || n > max) {
    return {
      ok: false,
      error: `${label} must be a whole number between ${min} and ${max}.`,
    };
  }
  return { ok: true, value: n };
}

export function ModuleAdoptionPage() {
  const navigate = useNavigate();
  const draft_q = use_draft();
  const saved_q = use_adoption_details({ enabled: !!draft_q.data });
  const save_m = use_save_adoption_details();
  const [form, set_form] = useState<Form>(EMPTY);
  const [prefilled, set_prefilled] = useState(false);
  const [error, set_error] = useState<string | null>(null);
  const [field_errors, set_field_errors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!draft_q.isFetched) return;
    if (!draft_q.data) {
      navigate('/onboarding/start', { replace: true });
      return;
    }
    if (!draft_q.data.address_completed) {
      navigate('/onboarding/address', { replace: true });
      return;
    }
    if (!draft_q.data.module_types.includes('ADOPTION')) {
      navigate(next_module_step_after('ADOPTION', draft_q.data.module_types), {
        replace: true,
      });
    }
  }, [draft_q.isFetched, draft_q.data, navigate]);

  useEffect(() => {
    if (prefilled) return;
    if (saved_q.data) {
      set_form({
        organization_types: saved_q.data.organization_types,
        contact_person_name: saved_q.data.contact_person_name,
        designation: saved_q.data.designation ?? '',
        animals_available:
          saved_q.data.animals_available !== null
            ? String(saved_q.data.animals_available)
            : '',
        max_capacity:
          saved_q.data.max_capacity !== null ? String(saved_q.data.max_capacity) : '',
        volunteer_count:
          saved_q.data.volunteer_count !== null
            ? String(saved_q.data.volunteer_count)
            : '',
        year_established: saved_q.data.year_established ?? '',
        staff_count: String(saved_q.data.staff_count),
      });
      set_prefilled(true);
    }
  }, [saved_q.data, prefilled]);

  function update<K extends keyof Form>(key: K, value: Form[K]) {
    set_form((f) => ({ ...f, [key]: value }));
  }

  /**
   * Toggle a pill *inside* the updater. Computing `toggle(form.x, v)` at the call
   * site reads the render closure, so several picks landing in one React batch
   * all start from the same stale array and only the last one survives.
   */
  function toggle_in<K extends ArrayKeys<Form>>(key: K, value: Form[K][number]) {
    set_form((f) => ({ ...f, [key]: toggle(f[key] as Form[K][number][], value) }));
  }

  async function handle_submit(e: React.FormEvent) {
    e.preventDefault();
    set_error(null);

    if (form.organization_types.length < 1) {
      set_error('Pick at least one organization type.');
      return;
    }
    if (form.contact_person_name.trim().length < 2) {
      set_error('Contact person name must be at least 2 characters.');
      return;
    }

    const animals = parse_optional_int(form.animals_available, 0, 10000, 'Animals available');
    if (!animals.ok) return set_error(animals.error);
    const capacity = parse_optional_int(form.max_capacity, 0, 10000, 'Max capacity');
    if (!capacity.ok) return set_error(capacity.error);
    const volunteers = parse_optional_int(form.volunteer_count, 0, 10000, 'Volunteer count');
    if (!volunteers.ok) return set_error(volunteers.error);

    const staff_count_n = Number(form.staff_count);
    if (!Number.isInteger(staff_count_n) || staff_count_n < 1 || staff_count_n > 10000) {
      set_error('Staff count must be a whole number between 1 and 10000.');
      return;
    }

    try {
      await save_m.mutateAsync({
        organization_types: form.organization_types,
        contact_person_name: form.contact_person_name.trim(),
        designation: form.designation === '' ? undefined : form.designation,
        animals_available: animals.value,
        max_capacity: capacity.value,
        volunteer_count: volunteers.value,
        year_established:
          form.year_established === '' ? undefined : form.year_established,
        staff_count: staff_count_n,
      });
      if (!draft_q.data) return;
      navigate(next_module_step_after('ADOPTION', draft_q.data.module_types));
    } catch (err) {
      set_field_errors(field_errors_from(err));
      set_error(
        err instanceof ApiError
          ? form_error_message(err, 'Could not save adoption details', { highlights_fields: true })
          : 'Could not save adoption details',
      );
    }
  }

  function digits_only(raw: string, max_len = 5): string {
    return raw.replace(/\D/g, '').slice(0, max_len);
  }

  return (
    <form onSubmit={handle_submit}>
      <StepSection
        step_key="module-adoption"
        title="Adoption center details"
        description="Tell us about your organization, capacity, and team."
        error={error}
        footer={
          <Button
            type="submit"
            variant="gradient"
            disabled={save_m.isPending}
            data-testid="module-adoption-submit"
          >
            {save_m.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Save & continue
          </Button>
        }
      >
        <div className="flex flex-col gap-2">
          <Label>Organization type (pick 1–4)</Label>
          <div className="flex flex-wrap gap-2" data-testid="adoption-org-types">
            {ADOPTION_ORG_TYPE_OPTIONS.map((o) => {
              const active = form.organization_types.includes(o.value);
              return (
                <button
                  key={o.value}
                  type="button"
                  data-testid={`adoption-org-${o.value}`}
                  data-checked={active}
                  onClick={() =>
                    toggle_in('organization_types', o.value)
                  }
                  className={
                    'rounded-full border px-3 py-1.5 text-sm transition-colors ' +
                    (active
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-input bg-card text-foreground hover:bg-muted')
                  }
                >
                  {o.label}
                </button>
              );
            })}
          </div>
        </div>

        <TextField
          label="Contact person name"
          name="contact_person_name"
          error={field_errors.contact_person_name}
          data-testid="field-adoption-contact-name"
          value={form.contact_person_name}
          onChange={(e) => update('contact_person_name', e.target.value)}
          required
          minLength={2}
          maxLength={120}
          placeholder="Sneha Kapoor"
        />

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <div className="flex flex-col gap-2">
            <Label htmlFor="designation">Designation</Label>
            <SelectField
              id="designation"
              data-testid="field-adoption-designation"
              value={form.designation}
              onValueChange={(v) => update('designation', v as AdoptionDesignation | '')}
              options={[
                { value: '', label: '— Not specified —' },
                ...ADOPTION_DESIGNATION_OPTIONS,
              ]}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="year_established">Year established</Label>
            <SelectField
              id="year_established"
              data-testid="field-adoption-year-established"
              value={form.year_established}
              onValueChange={(v) => update('year_established', v as YearBracket | '')}
              options={[
                { value: '', label: '— Not specified —' },
                ...YEAR_BRACKET_OPTIONS,
              ]}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <TextField
            label="Animals available"
            name="animals_available"
            data-testid="field-adoption-animals-available"
            value={form.animals_available}
            onChange={(e) => update('animals_available', digits_only(e.target.value))}
            inputMode="numeric"
            placeholder="42"
            optional
            hint="Current count of adoptable animals. 0–10000."
          />
          <TextField
            label="Max capacity"
            name="max_capacity"
            data-testid="field-adoption-max-capacity"
            value={form.max_capacity}
            onChange={(e) => update('max_capacity', digits_only(e.target.value))}
            inputMode="numeric"
            placeholder="100"
            optional
            hint="Total animals you can house. 0–10000."
          />
        </div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <TextField
            label="Volunteer count"
            name="volunteer_count"
            data-testid="field-adoption-volunteer-count"
            value={form.volunteer_count}
            onChange={(e) => update('volunteer_count', digits_only(e.target.value))}
            inputMode="numeric"
            placeholder="25"
            optional
            hint="Active volunteers. 0–10000."
          />
          <TextField
            label="Staff count"
            name="staff_count"
            error={field_errors.staff_count}
            data-testid="field-adoption-staff-count"
            value={form.staff_count}
            onChange={(e) => update('staff_count', digits_only(e.target.value))}
            inputMode="numeric"
            required
            placeholder="5"
            hint="Paid staff. 1–10000."
          />
        </div>
      </StepSection>
    </form>
  );
}
